import * as R from 'ramda'
import { getDateOfBirthDisplay } from 'sidecar-utility-library'

/**
 * Given a member, returns either that members name or a replacement based on
 * the index number of that member.
 *
 * @param {object} member The member to process
 * @param {int} index The index of the member (if used in array)
 * @returns {string}
 */
export const getMemberNameOrIndex = (index, member = {}) => {
  const firstName = R.pathOr('', ['firstName'], member)
  const lastName = R.pathOr('', ['lastName'], member)

  if (firstName || lastName) {
    return `${firstName} ${lastName}`.trim()
  } else if (!index) {
    return 'You'
  } else if (typeof index === 'string') {
    return `Dependent ${index}`
  } else if (typeof index === 'number' && index > 0) {
    return `Dependent #${index}`
  }
}

/**
 * Given a member, returns their date of birth with the proper formatting
 *
 * @param {object} member - Details
 * @returns {string | null} FormattedDob - 'MM/DD/YYYY' | ''
 */
export const getDob = R.pipe(
  R.propOr('', 'dateOfBirth'),
  R.ifElse(R.either(R.isEmpty, R.isNil), R.always(''), getDateOfBirthDisplay),
)

/** Sort members based on if its primary and then by their id */
export const getSortedMembers = members =>
  members.sort((a, b) => {
    if (a.primary) return -1 // if a is primary, the index of a must be lower than b
    if (b.primary) return 1 // if b is primary, the index of a must be larger than b
    // if neither a or b is primary, we will need to compare id (or create time)
    return a.id - b.id // otherwise, sort ascendingly by id which is incremental and indicates create time
  })

/**
 * Sorts the members in a coverage model to provide a consistent order.
 *
 * @param {obj} payload A coverage model
 */
export const sortMembersInCoverage = payload =>
  R.evolve({ members: getSortedMembers }, payload)

/**
 * Marks fields as selected if a user has the value in their medical history.
 *
 * @param {obj} member The member to init med history on
 * @param {arr} healthQuestions The health questions to preselect
 */
export const initializeMedicalHistory = (applicant, healthQuestions) => {
  if (!applicant.medicalHistory) return
  if (!healthQuestions) return

  const {
    medicalHistory: { answers, procedures, prescriptions, voluntaryExclusion },
  } = applicant

  const values = {
    procedures: procedures || '',
    prescriptions:
      prescriptions && R.not(R.isEmpty(prescriptions))
        ? prescriptions.map(a => ({ label: a.name, value: a }))
        : [],
    voluntaryExclusion: voluntaryExclusion || '',
  }

  // for each health question, check if the answer exists in
  // the members medical history, and mark as checked if it does.
  const inHistory = answers ? answers.map(a => a.uuid) : []
  const allAnswers = R.pipe(R.pluck('answers'), R.unnest)(healthQuestions)
  allAnswers.forEach(answer => {
    values[answer.uuid] = inHistory.indexOf(answer.uuid) > -1 ? answer : false
  })

  return values
}
